import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import PublicStores from "@/pages/store/PublicStores";
import {
  Calculator, Utensils, Stethoscope, Pill, Factory, ShoppingCart, Sparkles, ArrowLeft,
  Globe, Zap, Shield, CheckCircle2, Users, TrendingUp, Bot, ChevronDown,
  ShoppingBasket, Croissant, Boxes, Building2, Receipt, PackageCheck, Layers, Coins,
} from "lucide-react";

const NAVY = "hsl(224,76%,33%)";

const VERTICALS = [
  { icon: ShoppingCart, name: "متجر تجزئة", color: "from-blue-500 to-cyan-500" },
  { icon: Utensils, name: "مطعم وكافيه", color: "from-orange-500 to-red-500" },
  { icon: Pill, name: "صيدلية", color: "from-emerald-500 to-teal-500" },
  { icon: Stethoscope, name: "عيادة طبية", color: "from-pink-500 to-rose-500" },
  { icon: Factory, name: "مصنع وإنتاج", color: "from-slate-500 to-slate-700" },
  { icon: Calculator, name: "مكتب محاسبة", color: "from-purple-500 to-indigo-500" },
  { icon: ShoppingBasket, name: "سوبر ماركت", color: "from-lime-500 to-green-600" },
  { icon: Croissant, name: "مخبز وحلويات", color: "from-amber-500 to-yellow-600" },
  { icon: Boxes, name: "تجارة الجملة", color: "from-indigo-500 to-blue-700" },
];

const FEATURES = [
  { icon: Sparkles, title: "9 قطاعات في نظام واحد", desc: "متجر + مطعم + صيدلية + عيادة + مصنع + سوبرماركت + مخبز + محاسبة + جملة — كل ذلك بترخيص واحد", color: "text-amber-500" },
  { icon: Bot, title: "ذكاء اصطناعي مدمج", desc: "Claude يقترح ردود التذاكر + يكشف الاحتيال + يولّد وصفات طبية بضغطة واحدة", color: "text-purple-500" },
  { icon: Globe, title: "31 عملة + صرف تلقائي", desc: "تحويل نقود بين الصناديق بأسعار الصرف اليومية تلقائياً — مع بيع بالوزن والقيمة", color: "text-emerald-500" },
  { icon: Zap, title: "PWA يعمل على كل الأجهزة", desc: "بدون تنصيب — كمبيوتر، جوال، تابلت. إشعارات فورية بالصوت حتى والتطبيق مغلق", color: "text-cyan-500" },
  { icon: Shield, title: "أمان بمستوى بنكي", desc: "JWT + مصادقة ثنائية للمدراء + عزل كامل بين الشركات + قفل الفترات المحاسبية", color: "text-red-500" },
  { icon: Users, title: "متعدد المستخدمين والأدوار", desc: "عشرات الأدوار — كاشير، محاسب، صيدلي، طبيب، ممرض، نادل، شيف، مدقق...", color: "text-blue-500" },
];

const RESTAURANT_FEATURES = ["إدارة الطاولات مع 4 حالات", "شاشة KDS للمطبخ مع جرس تنبيه", "قائمة QR عامة قابلة للطباعة", "تقسيم الفاتورة بين الزبائن", "طلبات متعددة لكل طاولة"];
const CLINIC_FEATURES = ["مواعيد بحالات متعددة", "ملفات مرضى كاملة (حساسية، أمراض مزمنة)", "وصفات طبية + طباعة ℞", "قوالب وصفات جاهزة", "تحصيل رسوم = فاتورة تلقائية"];
const PHARMACY_FEATURES = ["تحذير ℞ عند بيع دواء بوصفة", "دفعات وتواريخ صلاحية", "صرف وصفات من العيادة", "سجل أدوية لكل مريض", "تنبيهات صلاحية استباقية"];

const STAT_METRICS = [
  { key: "companies", label: "شركة ونشاط نشط", icon: Building2, color: "from-blue-600 to-indigo-600", fallback: 0 },
  { key: "users", label: "مستخدم معتمد", icon: Users, color: "from-purple-600 to-indigo-600", fallback: 0 },
  { key: "invoices", label: "فاتورة ومعاملة معالجة", icon: Receipt, color: "from-emerald-600 to-teal-600", fallback: 0 },
  { key: "products", label: "صنف ومادة مُدارة", icon: PackageCheck, color: "from-amber-500 to-orange-600", fallback: 0 },
  { key: "sectors", label: "قطاعات متخصصة", icon: Layers, color: "from-cyan-600 to-blue-600", fixed: 9 },
  { key: "currencies", label: "عملة + صرف تلقائي", icon: Coins, color: "from-teal-600 to-emerald-600", fixed: 31 },
];

function useCountUp(target, run, dur = 1400) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!run) return;
    let raf; const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / dur);
      setVal(Math.floor(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick); else setVal(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, run, dur]);
  return val;
}

function StatCard({ metric, value, run, idx }) {
  const Icon = metric.icon;
  const n = useCountUp(value, run);
  return (
    <div
      className="relative rounded-2xl bg-white border border-slate-200/80 p-5 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
      style={{ animationDelay: `${idx * 70}ms` }}
      data-testid={`stat-${metric.key}`}
    >
      <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${metric.color} grid place-items-center mb-3 shadow-inner`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className={`text-3xl font-black tabular-nums bg-gradient-to-br ${metric.color} bg-clip-text text-transparent`}>
        {n.toLocaleString("ar-EG")}<span className="text-xl">+</span>
      </div>
      <div className="text-xs text-slate-500 font-medium mt-1 leading-snug">{metric.label}</div>
    </div>
  );
}

export default function Landing() {
  const [scrolled, setScrolled] = useState(false);
  const [faqOpen, setFaqOpen] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsRun, setStatsRun] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    api.get("/public/stats").then(({ data }) => { setStats(data); setStatsRun(true); }).catch(() => { setStats({}); setStatsRun(true); });
  }, []);

  // Start count-up when the stats band scrolls into view
  useEffect(() => {
    if (!statsRef.current) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) { setStatsRun(true); obs.disconnect(); }
    }, { threshold: 0.3 });
    obs.observe(statsRef.current);
    return () => obs.disconnect();
  }, [stats]);

  const faq = [
    { q: "هل يمكنني إدارة أكثر من نشاط بحساب واحد؟", a: "نعم! كل حساب يستوعب شركات متعددة، ولكل شركة نوع نشاطها الخاص. قد تدير مطعمك وصيدليتك من نفس الحساب — والقائمة الجانبية تتغيّر تلقائياً حسب النشاط." },
    { q: "هل يعمل بدون إنترنت؟", a: "نعم يعمل ضمن شبكتك المحلية (LAN) أو عبر Docker على استضافتك الخاصة، كما يدعم PWA لتصفح آخر البيانات المُحمّلة." },
    { q: "كيف أعرف التكلفة؟", a: "تواصل معنا عبر واتساب أو الإيميل وسنُعدّ لك عرضاً مخصصاً حسب حجم نشاطك وعدد المستخدمين والقطاعات التي تحتاجها." },
    { q: "هل يوجد تجربة مجانية؟", a: "نعم — 14 يوم كامل بدون بطاقة ائتمان. اضغط 'ابدأ تجربتك' وستحصل على كل الميزات فوراً بدون التزام." },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900" dir="rtl" data-testid="landing-page" style={{ fontFamily: "Tajawal, Cairo, sans-serif" }}>
      {/* Nav */}
      <nav className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "backdrop-blur-xl bg-white/80 border-b border-slate-200/80 shadow-sm" : "bg-transparent"}`}>
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-white border border-slate-200 grid place-items-center overflow-hidden shadow-sm"><img src="/brand/logo-mark.png" alt="محاسبتي" className="w-full h-full object-contain p-0.5" /></div>
            <span className="font-black text-lg" style={{ color: NAVY }}>محاسبتي</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login"><Button variant="ghost" size="sm" data-testid="nav-login">تسجيل الدخول</Button></Link>
            <Link to="/login"><Button size="sm" className="text-white shadow-md hover:brightness-110 transition" style={{ background: NAVY }} data-testid="nav-cta">ابدأ مجاناً</Button></Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-36 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(37,64,143,0.12),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(16,140,90,0.08),transparent_50%)]" />
        <div className="max-w-4xl mx-auto text-center relative">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-semibold mb-6 shadow-sm" style={{ color: NAVY }}>
            <Sparkles className="w-3.5 h-3.5" /> نظام إداري ومحاسبي عربي متكامل — يتشكّل حسب نشاطك
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 leading-[1.15] tracking-tight">
            الإدارة والمحاسبة أولاً<br />
            <span className="bg-gradient-to-l from-emerald-600 via-[hsl(224,76%,40%)] to-purple-600 bg-clip-text text-transparent">وكل نشاط بأدواته المستقلة</span>
          </h1>
          <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto mb-8 leading-relaxed">
            أساس <b className="text-slate-900">محاسبتي</b> إدارة مالية ومحاسبية كاملة — فواتير، صناديق، تقارير، موازنات ومخزون. وعند فتح كل شركة تختار نشاطها، فتظهر أدواته الخاصة وحده: المطعم يرى شاشة المطبخ والطاولات، والصيدلية ترى الوصفات والصلاحيات، والعيادة ترى المواعيد والمرضى — كلٌّ مستقل تماماً.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to="/login"><Button size="lg" className="text-base gap-2 h-12 px-8 text-white shadow-lg hover:brightness-110 transition" style={{ background: NAVY }} data-testid="hero-cta">ابدأ تجربتك المجانية 14 يوم <ArrowLeft className="w-4 h-4" /></Button></Link>
            <a href="#features"><Button size="lg" variant="outline" className="text-base h-12 border-slate-300" data-testid="hero-features">شاهد الميزات</Button></a>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6 mt-8 text-xs text-slate-500">
            <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> بدون بطاقة ائتمان</span>
            <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> إلغاء في أي وقت</span>
            <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> 3 لغات (عربي/إنجليزي/تركي)</span>
          </div>
        </div>
      </section>

      {/* Live Stats Band */}
      <section className="px-4 -mt-6" ref={statsRef}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-full">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              أرقام حيّة من قاعدة البيانات
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {STAT_METRICS.map((m, i) => {
              const val = m.fixed != null ? m.fixed : (stats ? (stats[m.key] ?? m.fallback) : m.fallback);
              return <StatCard key={m.key} metric={m} value={val} run={statsRun && stats !== null} idx={i} />;
            })}
          </div>
          <p className="text-center text-xs text-slate-400 mt-4 max-w-2xl mx-auto">
            🔒 البيانات مستخرجة لحظياً وتراكمياً من قاعدة بيانات محاسبتي التشغيلية — بكل شفافية ومصداقية وبدون تضخيم.
          </p>
        </div>
      </section>

      {/* Verticals */}
      <PublicStores />
      <section className="py-16 px-4 mt-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 tracking-tight">أساس محاسبي واحد — أدوات مستقلة لكل نشاط</h2>
            <p className="text-slate-600">الإدارة والمحاسبة قلب النظام، وفوقها تُضاف أدوات نشاطك وحدها دون خلط مع القطاعات الأخرى</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {VERTICALS.map((v, i) => {
              const Icon = v.icon;
              return (
                <Card key={i} className="p-4 border-slate-200/80 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group cursor-pointer bg-white" data-testid={`vertical-${i}`}>
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${v.color} grid place-items-center mb-3 group-hover:scale-110 transition-transform duration-300 shadow-md`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="font-bold text-sm">{v.name}</div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-16 px-4 bg-white border-y border-slate-100">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 tracking-tight">لماذا محاسبتي؟</h2>
            <p className="text-slate-600">ميزات لن تجدها مجتمعة في أي نظام عربي آخر</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <Card key={i} className="p-6 border-slate-200/80 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 bg-white" data-testid={`feature-${i}`}>
                  <div className="w-12 h-12 rounded-xl bg-slate-50 border border-slate-100 grid place-items-center mb-4">
                    <Icon className={`w-6 h-6 ${f.color}`} />
                  </div>
                  <h3 className="font-bold text-lg mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Vertical Deep-Dives */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 tracking-tight">تعمّق في كل قطاع</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <Card className="p-6 border-orange-200 bg-orange-50/40 hover:shadow-xl transition-all duration-300" data-testid="deep-restaurant">
              <div className="flex items-center gap-2 mb-4"><Utensils className="w-6 h-6 text-orange-600" /><h3 className="font-bold text-xl">للمطاعم والكافيهات</h3></div>
              <ul className="space-y-2">{RESTAURANT_FEATURES.map((f, i) => <li key={i} className="flex items-start gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" /><span>{f}</span></li>)}</ul>
            </Card>
            <Card className="p-6 border-pink-200 bg-pink-50/40 hover:shadow-xl transition-all duration-300" data-testid="deep-clinic">
              <div className="flex items-center gap-2 mb-4"><Stethoscope className="w-6 h-6 text-pink-600" /><h3 className="font-bold text-xl">للعيادات الطبية</h3></div>
              <ul className="space-y-2">{CLINIC_FEATURES.map((f, i) => <li key={i} className="flex items-start gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-pink-500 mt-0.5 flex-shrink-0" /><span>{f}</span></li>)}</ul>
            </Card>
            <Card className="p-6 border-emerald-200 bg-emerald-50/40 hover:shadow-xl transition-all duration-300" data-testid="deep-pharmacy">
              <div className="flex items-center gap-2 mb-4"><Pill className="w-6 h-6 text-emerald-600" /><h3 className="font-bold text-xl">للصيدليات</h3></div>
              <ul className="space-y-2">{PHARMACY_FEATURES.map((f, i) => <li key={i} className="flex items-start gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" /><span>{f}</span></li>)}</ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact for Pricing */}
      <section id="pricing" className="py-16 px-4 bg-white border-y border-slate-100">
        <div className="max-w-3xl mx-auto">
          <Card className="p-8 border-slate-200 text-center shadow-lg" style={{ background: "linear-gradient(135deg, hsl(224,76%,97%), #ffffff 45%, hsl(160,84%,96%))" }}>
            <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white border border-slate-200 text-xs font-semibold mb-3" style={{ color: NAVY }}>
              <Sparkles className="w-3 h-3" /> باقات مخصصة لكل نشاط
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold mb-3 tracking-tight">تواصل معنا للتسعير</h2>
            <p className="text-slate-600 mb-6 max-w-lg mx-auto leading-relaxed">
              نُصمّم لك باقة تناسب حجم نشاطك بالضبط — عدد الشركات، المستخدمين، والقطاعات. تجربة مجانية 14 يوم بدون التزام أو بطاقة ائتمان.
            </p>
            <div className="grid sm:grid-cols-2 gap-3 max-w-md mx-auto">
              <a href="https://wa.me/963951714393?text=مرحباً، أريد الاستفسار عن محاسبتي" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="w-full h-12 gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="whatsapp-btn">💬 واتساب</Button>
              </a>
              <a href="mailto:muazmusellem.workmail@gmail.com?subject=استفسار عن محاسبتي">
                <Button size="lg" variant="outline" className="w-full h-12 gap-2 border-slate-300" data-testid="email-btn">✉️ الإيميل</Button>
              </a>
            </div>
            <Link to="/login" className="block mt-4">
              <Button variant="ghost" size="sm" className="text-xs text-slate-500" data-testid="try-free-btn">أو ابدأ تجربة مجانية 14 يوم ←</Button>
            </Link>
          </Card>
        </div>
      </section>

      {/* Comparison */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 tracking-tight">لماذا محاسبتي وليس المنافسين؟</h2>
          </div>
          <div className="overflow-x-auto rounded-2xl shadow-sm border border-slate-200">
            <table className="w-full bg-white overflow-hidden">
              <thead className="text-sm text-white" style={{ background: NAVY }}>
                <tr>
                  <th className="p-3.5 text-start">الميزة</th>
                  <th className="p-3.5">محاسبتي</th>
                  <th className="p-3.5">الأمين</th>
                  <th className="p-3.5">وكيد</th>
                  <th className="p-3.5">Odoo</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {[
                  ["مطعم + KDS كامل", "✅", "❌", "❌", "Add-on مدفوع"],
                  ["عيادة + مواعيد + Rx", "✅", "❌", "❌", "Add-on مدفوع"],
                  ["ذكاء اصطناعي مدمج", "✅", "❌", "❌", "جزئي"],
                  ["31 عملة + صرف تلقائي", "✅", "4 عملات", "3-5", "✅"],
                  ["بيع بالوزن والقيمة", "✅", "❌", "جزئي", "❌"],
                  ["PWA للجوال + إشعارات", "✅", "❌", "✅", "✅"],
                ].map((row, i) => (
                  <tr key={i} className="border-b last:border-0 odd:bg-slate-50/50">
                    {row.map((cell, j) => (
                      <td key={j} className={`p-3.5 ${j === 0 ? "text-start font-medium" : "text-center"} ${j === 1 && cell === "✅" ? "text-emerald-600 font-bold text-lg" : ""}`}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 bg-white border-t border-slate-100">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 tracking-tight">أسئلة شائعة</h2>
          </div>
          <div className="space-y-2">
            {faq.map((item, i) => (
              <Card key={i} className="overflow-hidden border-slate-200/80 shadow-sm" data-testid={`faq-${i}`}>
                <button onClick={() => setFaqOpen(faqOpen === i ? null : i)} className="w-full text-start p-4 flex items-center justify-between gap-2 hover:bg-slate-50 transition">
                  <span className="font-bold">{item.q}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${faqOpen === i ? "rotate-180" : ""}`} style={{ color: NAVY }} />
                </button>
                {faqOpen === i && <div className="px-4 pb-4 text-sm text-slate-600 leading-relaxed">{item.a}</div>}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 text-white text-center relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${NAVY}, hsl(224,76%,28%) 55%, hsl(160,70%,28%))` }}>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.12),transparent_45%)]" />
        <div className="max-w-2xl mx-auto relative">
          <TrendingUp className="w-12 h-12 mx-auto mb-4 opacity-90" />
          <h2 className="text-3xl sm:text-4xl font-black mb-4 tracking-tight">ابدأ رحلتك اليوم</h2>
          <p className="text-lg opacity-90 mb-8">نظام واحد يدير كل أنشطتك — جرّبه مجاناً 14 يوماً كاملة بدون التزام</p>
          <Link to="/login"><Button size="lg" variant="secondary" className="text-base h-12 px-8 gap-2 shadow-2xl" data-testid="final-cta">ابدأ مجاناً — 14 يوم كاملة <ArrowLeft className="w-4 h-4" /></Button></Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-4 bg-slate-900 text-slate-300 text-sm">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-white grid place-items-center overflow-hidden"><img src="/brand/logo-mark.png" alt="محاسبتي" className="w-full h-full object-contain p-0.5" /></div>
            <span className="font-bold text-white">محاسبتي</span>
          </div>
          <div className="text-xs opacity-70">© 2026 محاسبتي · جميع الحقوق محفوظة · <a href="mailto:muazmusellem.workmail@gmail.com" className="underline">تواصل معنا</a></div>
        </div>
      </footer>
    </div>
  );
}
